/**
 * Shared hook for the employee's Replacement leave cap.
 *
 * Extracted from ApplyLeavePage so that HeroBalanceCard, MyLeavePage, and
 * ApplyLeavePage all read the same query key and share the same cached result.
 *
 * Endpoints (impersonation-aware, mirroring listMyBalances / listEmployeeBalances):
 *   - self:         GET /api/v1/me/replacement-cap
 *   - "Viewing as": GET /api/v1/employees/{id}/replacement-cap
 * Both respond: { remainingDays: number; nearestExpiry: string | null; grants: [...] }
 *
 * The `/me` endpoint resolves the real actor (current_actor), so under
 * "Viewing as" it would return the HR viewer's own cap — same reason the
 * balance tiles beside it switch to the employee-scoped endpoint.
 */
import { useQuery } from '@tanstack/react-query'
import { z } from 'zod'
import { apiRequest } from './client'

// ── Zod schema (Zod at every boundary per CLAUDE.md) ─────────────────────────

// Pydantic serialises Decimal as a JSON string ("0", "3.00") — not a number.
// `z.coerce.number()` accepts both shapes so the schema survives the wire
// format inconsistency documented in memory (project_lms_wire_format_inconsistent).
const ReplacementGrantBreakdownSchema = z.object({
  id: z.string(),
  remaining: z.coerce.number(),
  expiresAt: z.string(),
})

export const ReplacementCapSchema = z.object({
  remainingDays: z.coerce.number(),
  nearestExpiry: z.string().nullable(),
  grants: z.array(ReplacementGrantBreakdownSchema),
})

export type ReplacementCapData = z.infer<typeof ReplacementCapSchema>

// ── Fetcher ───────────────────────────────────────────────────────────────────

async function fetchReplacementCap(
  employeeId?: string | null,
): Promise<ReplacementCapData> {
  const path = employeeId
    ? `/api/v1/employees/${encodeURIComponent(employeeId)}/replacement-cap`
    : '/api/v1/me/replacement-cap'
  const raw = await apiRequest<unknown>(path)
  return ReplacementCapSchema.parse(raw)
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export const REPLACEMENT_CAP_QUERY_KEY = ['me', 'replacement-cap'] as const

export interface UseReplacementCapResult {
  data: ReplacementCapData | undefined
  isPending: boolean
  isError: boolean
}

/**
 * TanStack Query hook that returns the employee's replacement leave cap.
 *
 * @param employeeId  When set (HR "Viewing as"), reads that employee's cap via
 *   the employee-scoped endpoint. When null/undefined, reads the signed-in
 *   user's own cap via `/me`. The query key includes the id so switching the
 *   impersonation target refetches rather than serving a stale tile.
 *
 * - `staleTime: 5min` — grant totals don't change in real-time.
 * - Always enabled (no type-gate): the home dashboard needs this on page
 *   load, not just when the user picks a leave type.
 */
export function useReplacementCap(employeeId?: string | null): UseReplacementCapResult {
  const { data, isPending, isError } = useQuery({
    queryKey: [...REPLACEMENT_CAP_QUERY_KEY, employeeId ?? 'self'],
    queryFn: () => fetchReplacementCap(employeeId),
    staleTime: 5 * 60_000,
  })
  return { data, isPending, isError }
}
